/**
 * SME Portal — View Assigned Assessment Domains (with delete)
 * Shows the actual domain assignments for each client organization,
 * derived from EM-approved assessment requests. History persists until
 * the SME manually deletes an assignment.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress, Alert, Accordion, AccordionSummary,
  AccordionDetails, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button,
} from '@mui/material';
import { ExpandMore, CheckCircle, Block, Visibility, Delete } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface DomainAssignment {
  category: string;
  domain_name: string;
  domain_id: string;
  assigned_at: string | null;
  assigned_by: string | null;
  request_id: string | null;
  priority: string | null;
}

interface CategoryGroup {
  category: string;
  domains: string[];
}

interface ClientOrg {
  id: string;
  name: string;
  domain: string;
  is_active: boolean;
  assignments: DomainAssignment[];
  categories: CategoryGroup[];
  total_assigned: number;
}

const AssignedDomains: React.FC = () => {
  const [orgs, setOrgs] = useState<ClientOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ orgId: string; orgName: string; domainId: string; domainName: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = () => {
    setLoading(true);
    authFetch('/api/sme/client-domain-assignments')
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setOrgs(data.organizations || []);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `/api/sme/client-domain-assignments/${deleteTarget.orgId}/${deleteTarget.domainId}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setSuccess(`Removed "${deleteTarget.domainName}" from ${deleteTarget.orgName}`);
        setDeleteTarget(null);
        fetchData();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to remove assignment');
      }
    } catch {
      setError('Failed to remove assignment');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Visibility sx={{ color: '#483698' }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Assigned Client Assessment Domains
        </Typography>
          <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Assessment domains assigned to each client organization. History is retained until you manually remove an entry.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {orgs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No assigned client organizations found. Contact an Admin to assign you to client engagements.
          </Typography>
        </Paper>
      ) : (
        orgs.map((org) => (
          <Accordion key={org.id} defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{org.name}</Typography>
                <Chip
                  label={org.domain || '—'}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={`${org.total_assigned} domain${org.total_assigned !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                {org.is_active ? (
                  <Chip icon={<CheckCircle />} label="Active" size="small" color="success" />
                ) : (
                  <Chip icon={<Block />} label="Disabled" size="small" color="error" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {org.assignments.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No assessment domains have been assigned to this client yet.
                  Clients can request domains via <strong>Assessment Requests</strong>, which the EM approves.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Assessment Domain</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Assigned</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 60 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {org.assignments.map((a) => (
                      <TableRow key={a.domain_id}>
                        <TableCell>
                          <Chip label={a.category} size="small" color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={a.domain_name} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove assignment">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget({
                                orgId: org.id,
                                orgName: org.name,
                                domainId: a.domain_id,
                                domainName: a.domain_name,
                              })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Domain Assignment</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.domainName}</strong> from{' '}
            <strong>{deleteTarget?.orgName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignedDomains;
